import type { Appointment } from "@/features/clinic/types/appointment";
import type { Contact } from "@/features/contact/types";
import type { Activity } from "@/types/activity";
import type { Conversation } from "@/types/conversation";
import type { User } from "@/types/user";

import { DateTime } from "luxon";

/**
 * Fixed name for the deterministic "parked" contact used in the F6 e2e
 * test. It has a future follow-up (tomorrow) and lastMessageOwner === "user"
 * so the park-until-due filter hides it from the default All Conversations
 * view but it remains visible under Follow-ups.
 */
export const FUTURE_FOLLOWUP_SEED_NAME = "Fatima Al-Rashidi";

/**
 * Guarantee the logged-in user has a little "My Inbox" work to show. Contact
 * assignment is otherwise random (≈70% spread across 8 users), and today may be
 * a weekend - which appointment generation skips - so the dashboard could
 * otherwise render entirely empty for the seeded user. Mutates the generated
 * arrays in place before they are stored.
 */
export const seedMyInboxDashboard = (
  conversations: Map<string, Conversation>,
  appointments: Appointment[],
  me: User,
  mentionAuthor: User,
  contacts: Contact[],
  activities: Map<string, Activity[]>,
): void => {
  const now = DateTime.now();

  // The store keys conversations by contact id; mutating the values mutates the
  // same objects the handlers read back.
  const list = Array.from(conversations.values());

  list.slice(0, 4).forEach((conversation) => {
    conversation.contact.assignedUserId = me.id;
    conversation.contact.assignedUser = me;
    conversation.contact.status = "open";
  });

  // Two awaiting-reply conversations (the contact sent the last message).
  [0, 3].forEach((index, order) => {
    const conversation = list[index];

    if (!conversation) return;

    conversation.contact.lastMessageOwner = "contact";
    conversation.contact.lastMessageProcessedAt =
      now.minus({ hours: 2 * order + 1 }).toISO() ??
      conversation.contact.lastMessageProcessedAt;
    conversation.contact.followUp = null;
  });

  // Two follow-ups - one overdue, one due later today.
  const followUps = [
    {
      index: 1,
      reason: "Send the revised quote",
      dueAt: now.minus({ hours: 3 }),
    },
    {
      index: 2,
      reason: "Confirm the pickup window",
      dueAt: now.plus({ hours: 5 }),
    },
  ];

  followUps.forEach(({ index, reason, dueAt }) => {
    const conversation = list[index];

    if (!conversation) return;

    conversation.contact.lastMessageOwner = "user";
    conversation.contact.followUp = {
      id: `fu-seed-${index}`,
      reason,
      dueAt: dueAt.toISO() ?? now.toISO() ?? "",
      source: "user",
      reminderSentAt: null,
    };
  });

  // Retarget two appointments to today (generation skips weekends).
  appointments.slice(0, 2).forEach((appointment, index) => {
    const start = now.set({
      hour: 10 + index * 2,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    appointment.startDate = start.toISO() ?? appointment.startDate;
    appointment.endDate =
      start.plus({ minutes: 30 }).toISO() ?? appointment.endDate;
    appointment.status = index === 0 ? "confirmed" : "pending";
  });

  // Guarantee each sidebar presence view (Mentions / Participating /
  // Unattended) has its own distinct, non-empty set of real conversations.
  // Disjoint index ranges keep the views from overlapping. Follow-ups are
  // already covered by the block above.

  // Mentions: a teammate @mentions the current user in a note. Stored as real
  // `@[Name](id)` markup so the surfaces that chip it render the seeded note
  // the same way they render a live one.
  const meMarkup = `@[${[me.firstName, me.lastName].filter(Boolean).join(" ")}](${me.id})`;

  list.slice(8, 10).forEach((conversation, order) => {
    const contactId = conversation.contact.id;
    const createdAt =
      now.minus({ hours: order + 1 }).toISO() ?? now.toISO() ?? "";
    const note: Activity = {
      id: `seed-mention-note-${order}`,
      userId: mentionAuthor.id,
      contactId,
      mentioned: [me.id],
      createdAt,
      type: "note",
      payload: {
        text:
          order === 0
            ? `${meMarkup} can you take a look, patient asked about financing options.`
            : `${meMarkup} flagging this one, needs a senior to confirm the quote.`,
      },
    };

    activities.set(contactId, [...(activities.get(contactId) ?? []), note]);
    conversation.contact.lastNote = note.payload.text;
    conversation.contact.lastNoteAt = createdAt;
  });

  // Participating: the current user has interacted with (but doesn't own) the
  // conversation - they appear as a participant.
  list.slice(10, 12).forEach((conversation, order) => {
    const { contact } = conversation;

    if (contact.interactions.some((it) => it.userId === me.id)) return;

    contact.interactions = [
      ...contact.interactions,
      {
        userId: me.id,
        interactedAt: now.minus({ hours: order + 1 }).toJSDate(),
        contactId: contact.id,
        unreadCount: 0,
      },
    ];
  });

  // Unattended: open, the contact sent the last message, and nobody owns it.
  list.slice(12, 14).forEach((conversation, order) => {
    const { contact } = conversation;

    contact.status = "open";
    contact.assignedUserId = null;
    contact.assignedUser = null;
    contact.interactions = [];
    contact.followUp = null;
    contact.lastMessageOwner = "contact";
    contact.lastMessageProcessedAt =
      now.minus({ hours: order + 1 }).toISO() ?? contact.lastMessageProcessedAt;
  });

  // Deterministic "parked" contact: a future follow-up (tomorrow) with no
  // unreplied inbound so the park-until-due filter always hides it from the
  // default All Conversations view. Named by FUTURE_FOLLOWUP_SEED_NAME for the
  // F6 e2e assertion.
  const parkedId = "seed-parked-future-followup";
  const parkedContact: Contact = {
    id: parkedId,
    firstName: "Fatima",
    lastName: "Al-Rashidi",
    username: null,
    email: "fatima.alrashidi@seed.test",
    phone: "+971500000001",
    address: null,
    language: "en",
    country: "AE",
    imageUrl: "",
    blocked: false,
    lifecycleId: null,
    origin: null,
    referredByName: null,
    sortOrder: null,
    assignedUserId: me.id,
    assignedUser: me,
    assignedTeamId: null,
    aiMode: "off",
    aiModeUntil: null,
    aiModeRevertTo: null,
    lastMessageProcessedAt: now.minus({ hours: 12 }).toISO() ?? now.toISO(),
    lastMessageOwner: "user",
    lastMessageId: "seed-parked-msg-1",
    lastBumpedAt: now.minus({ hours: 12 }).toISO() ?? now.toISO(),
    createdAt: now.minus({ days: 10 }).toISO() ?? now.toISO(),
    updatedAt: now.minus({ days: 10 }).toISO() ?? now.toISO(),
    tags: [],
    treatmentIds: [],
    productIds: [],
    adIds: [],
    websiteIds: [],
    channels: [],
    interactions: [],
    status: "open",
    snoozedUntil: null,
    followUp: {
      id: "seed-parked-fu-1",
      reason: "Check in after consultation",
      dueAt: now.plus({ days: 2 }).toISO() ?? now.toISO(),
      source: "user",
      reminderSentAt: null,
    },
    unreadCount: 0,
    unreadMentionCount: 0,
    lastSummary: null,
    lastNote: null,
    lastNoteAt: null,
    lastAd: null,
    lastWebsiteVisit: null,
    value: "0.00",
    humanAgentRequired: false,
    knowledgeGapPending: false,
    aiPending: null,
  };

  contacts.push(parkedContact);
  conversations.set(parkedId, { contact: parkedContact });
};
